// src/redux/slicers/siteSettingsSlicer.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../api/axiosInstance";
import {
  applySiteSettings,
  cacheSiteSettings,
} from "../../../utils/applySiteSettings";

/**
 * GET settings.
 * @param {Object} [params] - Optional query params for performance (backend supports these):
 *   - option_name: comma-separated (e.g. "about_us_title,about_us_content")
 *   - lang: language code (e.g. "en", "ar")
 *   - upload_keys: comma-separated option names to return as Upload (e.g. "about_us_image,why_us_video")
 * When params.option_name is provided, state.data is NOT updated (partial fetch for specific pages).
 */
export const fetchSiteSettings = createAsyncThunk(
  "siteSettings/fetch",
  async (params, thunkAPI) => {
    try {
      let url = "/dashboard/general-settings";
      if (params && typeof params === "object") {
        const q = new URLSearchParams();
        if (params.option_name) q.set("option_name", params.option_name);
        if (params.lang) q.set("lang", params.lang);
        if (params.upload_keys) q.set("upload_keys", params.upload_keys);
        const query = q.toString();
        if (query) url += "?" + query;
      }
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ----------- Async Thunk (UPDATE settings) -----------
export const updateSiteSettings = createAsyncThunk(
  "siteSettings/update",
  async (data, thunkAPI) => {
    try {
      const response = await api.post("/dashboard/general-settings", data);
      await thunkAPI.dispatch(fetchSiteSettings());
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ----------- Slice ----------- 
const siteSettingsSlicer = createSlice({
  name: "siteSettings",
  initialState: {
    data: [],
    loading: false,
    error: null,
    successMessage: "",
  },
  reducers: {
    clearSiteSettingsState: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
      state.successMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSiteSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSiteSettings.fulfilled, (state, action) => {
        state.loading = false;
        const isPartial = action.meta?.arg?.option_name;
        if (!isPartial) {
          state.data = action.payload;
          cacheSiteSettings(action.payload);
          applySiteSettings(action.payload);
        }
      })
      .addCase(fetchSiteSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateSiteSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSiteSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message || "";
      })
      .addCase(updateSiteSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSiteSettingsState } = siteSettingsSlicer.actions;
export default siteSettingsSlicer.reducer;
